import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ContractsService } from '../../../services/contracts.service';
import { B2bPortalRole, B2bPortalUsersService } from '../../../services/b2b-portal-users.service';
import { ErrorService } from '../../../shared/services/error.service';

@Component({
  selector: 'app-b2b-portal-user-form',
  templateUrl: './b2b-portal-user-form.component.html',
  styleUrls: ['./b2b-portal-user-form.component.css']
})
export class B2bPortalUserFormComponent implements OnInit {
  form: FormGroup;
  isLoading = false;
  isSubmitting = false;
  isEditMode = false;
  contractId: number | null = null;
  userId: number | null = null;
  contractNumber = '';
  contractName = '';
  roles: B2bPortalRole[] = [];
  rolesError = '';
  isLoadingRoles = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private contractsService: ContractsService,
    private portalUsers: B2bPortalUsersService,
    private errorService: ErrorService
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.minLength(6)]],
      roleCode: ['', Validators.required],
      active: [true]
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      this.userId = params['userId'] ? +params['userId'] : null;
      this.isEditMode = !!this.userId;

      if (!this.isEditMode) {
        this.form.get('password')?.setValidators([Validators.required, Validators.minLength(6)]);
      } else {
        this.form.get('password')?.setValidators([Validators.minLength(6)]);
        this.form.get('email')?.disable();
      }
      this.form.get('password')?.updateValueAndValidity();

      if (this.contractId) {
        this.loadContractInfo();
        this.loadRoles();
        if (this.userId) this.loadUser();
      }
    });
  }

  loadContractInfo(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (c) => {
        this.contractNumber = c.contractNumber || '';
        this.contractName = c.contractName || '';
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  loadRoles(): void {
    this.isLoadingRoles = true;
    this.rolesError = '';
    // Garante catálogo (roles/permissions) antes de listar — espelho do seed no access-center.
    this.portalUsers.seed().subscribe({
      next: () => this.fetchRoles(),
      error: () => this.fetchRoles()
    });
  }

  private fetchRoles(): void {
    this.portalUsers.listRoles().subscribe({
      next: (roles) => {
        this.roles = roles;
        this.isLoadingRoles = false;
        if (!roles.length) {
          this.rolesError =
            'Nenhum perfil encontrado. Abra “Atribuição de acessos” do contrato e sincronize o catálogo, ou crie um perfil.';
          return;
        }
        const current = this.form.get('roleCode')?.value;
        if (!current || !roles.some(r => r.code === current)) {
          this.form.patchValue({ roleCode: roles[0].code });
        }
      },
      error: (err) => {
        this.isLoadingRoles = false;
        this.roles = [];
        if (err?.status === 401) {
          this.rolesError =
            'Sem autorização no Auth B2B (StaffJwt). Confirme JwtAuth no ERP e StaffJwt no Auth B2B.';
          this.errorService.showError(this.rolesError, 'error');
          return;
        }
        this.rolesError = err?.error?.message || err?.error?.Message || 'Falha ao listar perfis do portal.';
        this.errorService.handleHttpError(err, this.rolesError);
      }
    });
  }

  loadUser(): void {
    if (!this.userId) return;
    this.isLoading = true;
    this.portalUsers.getById(this.userId).subscribe({
      next: (user) => {
        this.form.patchValue({
          name: user.name,
          email: user.email,
          roleCode: user.roleCode || 'OPERACIONAL',
          active: user.active
        });
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.errorService.handleHttpError(err);
      }
    });
  }

  submit(): void {
    if (this.form.invalid || !this.contractId) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const raw = this.form.getRawValue();

    if (this.isEditMode && this.userId) {
      const payload: any = {
        name: raw.name,
        roleCode: raw.roleCode,
        active: raw.active
      };
      if (raw.password) payload.password = raw.password;

      this.portalUsers.update(this.userId, payload).subscribe({
        next: () => this.navigateBack(),
        error: (err) => {
          this.isSubmitting = false;
          this.errorService.handleHttpError(err);
        }
      });
      return;
    }

    this.portalUsers.create({
      name: raw.name,
      email: raw.email,
      password: raw.password,
      b2BContractId: this.contractId,
      roleCode: raw.roleCode
    }).subscribe({
      next: () => this.navigateBack(),
      error: (err) => {
        this.isSubmitting = false;
        this.errorService.handleHttpError(err);
      }
    });
  }

  navigateBack(): void {
    if (!this.contractId) {
      this.router.navigate(['/b2b/contracts']);
      return;
    }
    this.router.navigate(['/b2b/contracts', this.contractId, 'portal-users']);
  }
}
